import React from 'react'

import * as c from '../../../../common'

import { columnWidths, fontFamily, fontSize } from './common'

export type DetailPlainLineProps = c.r<{
  /**
   * Render as a category divider: bold, centered label, bold prices. Jaron
   * (2026-09-07, 09-10, 09-22 "my 3rd time asking"): the Smart Home line must
   * read like the WiFi Camera divider above it, not as small plain text.
   */
  bold?: boolean
  isPDF: boolean
  label: string
  price: number
}>

// V62: the canonical formatter lives in common/helpers.
const money = c.money

/**
 * A Scope of Work row that is a label and a price, nothing else.
 *
 * `DetailLine` renders a real product row: quantity, notes, and a pencil that
 * opens a live editor on the underlying Firebase rows. The Smart Home line is
 * none of those things - it stands in for a whole category whose product names
 * must never reach the page (T56), and Jaron's own wording for it was "at the
 * same price, no descriptions". Handing it to `DetailLine` would give the
 * customer a pencil that opens the alarm equipment it exists to hide.
 *
 * Uses the same column widths, so it lines up under the same header.
 */
export default React.memo<DetailPlainLineProps>(function DetailPlainLine({
  bold = false,
  isPDF,
  label,
  price,
}) {
  const cell = React.useMemo(
    (): React.CSSProperties => ({
      color: '#000000',
      fontFamily,
      fontSize,
      fontWeight: bold ? 'bold' : undefined,
      margin: 0,
    }),
    [bold],
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
      <p style={{ ...cell, textAlign: 'center', width: columnWidths[0] }} />

      <p style={{ ...cell, textAlign: 'center', width: columnWidths[1] }}>
        {bold ? '' : '-'}
      </p>

      <p
        style={{
          ...cell,
          textAlign: bold ? 'center' : undefined,
          width: isPDF
            ? parseFloat(columnWidths[0]) + parseFloat(columnWidths[2]) + '%'
            : columnWidths[2],
        }}
      >
        {label}
      </p>

      <p style={{ ...cell, textAlign: 'end', width: columnWidths[3] }}>
        {money(price)}
      </p>

      <p style={{ ...cell, textAlign: 'end', width: columnWidths[4] }}>
        {money(price)}
      </p>
    </div>
  )
})
